/**
 * "Sign in with Shopify" on the login page.
 *
 * A Shopify install signs the seller in with a one-time token, so the
 * account has no password. When the seller comes back through the login
 * page and signs in with Shopify, we offer (never require) to set one so
 * they can use email + password next time.
 *
 * The intent survives the round trip through Shopify in localStorage
 * (Shopify may hand back in a new tab). The offer itself lives in
 * sessionStorage for the tab that finished signing in.
 */

const INTENT_KEY = "auth.shopifyLoginIntent";
const OFFER_KEY = "auth.offerSetPassword";
const INTENT_TTL_MS = 15 * 60 * 1000;
export const PASSWORD_PROMPT_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

const SHOP_HANDLE = /^[a-z0-9][a-z0-9-]*$/;

/**
 * Turn what a seller types into "<handle>.myshopify.com", or "" when it
 * can't be one. Accepts the handle, the myshopify domain, a full URL, or
 * an admin.shopify.com/store/<handle> link.
 */
export function normalizeShopDomain(input) {
  let value = String(input || "").trim().toLowerCase();
  if (!value) return "";
  value = value.replace(/^https?:\/\//, "");

  const adminMatch = value.match(/^admin\.shopify\.com\/store\/([^/?#]+)/);
  if (adminMatch) {
    return SHOP_HANDLE.test(adminMatch[1])
      ? `${adminMatch[1]}.myshopify.com`
      : "";
  }

  const host = value.split(/[/?#]/)[0];
  if (host.endsWith(".myshopify.com")) {
    const handle = host.slice(0, -".myshopify.com".length);
    return SHOP_HANDLE.test(handle) ? host : "";
  }
  if (SHOP_HANDLE.test(host)) return `${host}.myshopify.com`;
  return "";
}

const read = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const write = (storage, key, value) => {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    /* private mode */
  }
};

export function rememberShopifyLoginIntent(now = Date.now()) {
  write(window.localStorage, INTENT_KEY, String(now));
}

/** True (once) when this sign-in started from the login page's button. */
export function takeShopifyLoginIntent(now = Date.now()) {
  const raw = read(window.localStorage, INTENT_KEY);
  write(window.localStorage, INTENT_KEY, null);
  const at = Number(raw);
  return Number.isFinite(at) && at > 0 && now - at <= INTENT_TTL_MS;
}

export function markPasswordOffer() {
  write(window.sessionStorage, OFFER_KEY, "1");
}

export function hasPasswordOffer() {
  return read(window.sessionStorage, OFFER_KEY) === "1";
}

export function clearPasswordOffer() {
  write(window.sessionStorage, OFFER_KEY, null);
}

/** A Shopify-install account whose owner never chose a password. */
export function needsOwnPassword(user) {
  return user?.shopifyUser === true && user?.passwordSet !== true;
}

/** "Not now" was pressed within the last 30 days. */
export function passwordPromptSnoozed(user, now = Date.now()) {
  const at = Number(user?.passwordPromptDismissedAt);
  return Number.isFinite(at) && at > 0 && now - at < PASSWORD_PROMPT_SNOOZE_MS;
}
